// Directive ordering checks for `@char`, `@import`, and `@layer`.
// Rule target order: `@char` -> `@layer` -> `@import(.luis)` (css import is free-form).
import type { LuisCompileWarning } from '../types'
import type { DirectiveOrderState } from './order-types'
import type { ParsedImportRequest } from './types'
export type { DirectiveOrderState } from './order-types'

// Create initial directive-order state.
export function createDirectiveOrderState(): DirectiveOrderState {
  // Return the initial directive-order state.
  return { phase: 0 }
}

// Mark top-level non-directive content as phase 5.
export function markContentSeen(state: DirectiveOrderState): void {
  // Check whether the parser already passed the content section.
  if (state.phase >= 5) {
    // Return early when content phase was already reached.
    return
  } else {
    // Move to content phase after first non-header token.
    state.phase = 5
  }
}

// Validate and advance state for charset-like directives.
export function trackCharsetDirective(
  state: DirectiveOrderState,
  line: number,
  sourceId: string,
  warnings: LuisCompileWarning[],
): void {
  // Check whether `@charset` appears after any preceding top-level content.
  if (state.phase > 0) {
    warnings.push({
      code: 'invalid-directive-position',
      line,
      message: `[${sourceId}] @charset must be the first top-level statement.`,
    })
  } else {
    // Keep warning list unchanged for valid charset position.
  }

  state.phase = 1
}

// Validate and advance state for `@layer ...;` declarations.
export function trackLayerDirective(
  state: DirectiveOrderState,
  line: number,
  sourceId: string,
  warnings: LuisCompileWarning[],
): void {
  // Check whether `@layer` appears after `.luis` imports or content.
  if (state.phase >= 4) {
    warnings.push({
      code: 'invalid-directive-order',
      line,
      message: `[${sourceId}] @layer declaration must be before @import ".luis" and before regular rules.`,
    })
  } else {
    // Keep warning list unchanged for valid layer ordering.
  }

  // Check whether phase should advance into the layer section.
  if (state.phase < 3) {
    state.phase = 3
  } else {
    // Keep current phase when already in layer or later phases.
  }
}

// Validate and advance state for css/luis import directives.
export function trackImportDirective(
  state: DirectiveOrderState,
  request: ParsedImportRequest,
  line: number,
  sourceId: string,
  warnings: LuisCompileWarning[],
): void {
  // Check whether CSS or unknown imports should stay free-form.
  if (request.kind === 'css' || request.kind === 'other') {
    // Check whether the phase should advance into the import section.
    if (state.phase < 2) {
      state.phase = 2
    } else {
      // Keep current phase when already at import section.
    }
    // Return early for CSS or unknown import directives.
    return
  } else {
    // Continue `.luis` import order validation.
  }

  // Check whether phase should advance into the `.luis` import section.
  if (state.phase < 4) {
    state.phase = 4
  } else {
    // Keep phase when parser already reached `.luis`/content section.
  }
}
